import * as React from 'react'
import { connect } from 'react-redux'
import { handleOpeningHours } from '../../../utils/openingHours'
import type { RootState, Dispatch } from '../../../app/store'
import type { MenuT, StatusT } from '../../../models/menus'
import Page from '../../layout/Page'
import Loading from '../../Loading'
import MenuItem from '../../menus/item'
import { useLocation } from 'react-router-dom'
import styles from './Menus.module.css'
import { StoreT } from '../../../models/store'
import MessageBanner from '../../MessageBanner'
import Modal from '../../Modal'
import eatinIcon from '../../../images/eatin.svg'
import takeawayIcon from '../../../images/takeaway.svg'
import DeliveryItem from '../../DeliveryItem'
import { DELIVERY_ITEM_TYPE, DELIVERY_TYPE, DeliveryProviderT, DeliveryProvidersT, DeliveryType, getDeliveryDisplayText, isAvailable } from '../../../utils/deliveryHelper'

interface MenusT {
  status: StatusT,
  menuItems: Array<MenuT>
  store: StoreT | null
  selectDeliveryType: (payload: { type: typeof DELIVERY_TYPE.EATIN | typeof DELIVERY_TYPE.TAKEAWAY, deliveryProviderId?: string, deliveryProviderType?: number }) => void
  fetchActiveDeliveryProviders: (storeId: string) => Promise<DeliveryProvidersT>
  deliveryType: DeliveryType
  tableNumber: string | undefined
}

function Menus ({ status, menuItems, store, deliveryType, tableNumber, selectDeliveryType, fetchActiveDeliveryProviders }: MenusT) {
  const location = useLocation()
  const { startupMessage } = store || {}
  const [showDeliveryModal, setShowDeliveryModal] = React.useState(!deliveryType || deliveryType === 'IDLE')
  const [deliveryOption, setDeliveryOption] = React.useState<'eatin' | 'takeaway' | null>(null)
  const [deliveryProviders, setDeliveryProviders] = React.useState<DeliveryProvidersT>([])
  const [subProviders, setSubProviders] = React.useState<DeliveryProvidersT>([])
  const [showSubProviderOptions, setShowSubProviderOptions] = React.useState(false)
  const [selectedSubProvider, setSelectedSubProvider] = React.useState<DeliveryProviderT | null>(null)
  const [loading, setLoading] = React.useState(false)

  const deliveryDisplayText = getDeliveryDisplayText(deliveryOption)
  
  React.useEffect(() => {
    if (deliveryType === DELIVERY_TYPE.IDLE && !tableNumber) {
      setShowDeliveryModal(true)
      setDeliveryOption(null)
      setShowSubProviderOptions(false)
      setSelectedSubProvider(null)
      setSubProviders([])
    }
  }, [deliveryType, tableNumber])

  React.useEffect(() => {
    async function getDeliveryOptions (storeId: string) {
      if (tableNumber && deliveryType === DELIVERY_TYPE.IDLE) {
        // Handle QR code for table number (default to EATIN)
        setDeliveryOption('eatin')
        selectDeliveryType({ type: DELIVERY_TYPE.EATIN })
        setShowDeliveryModal(false)
        return
      }

      if (deliveryType !== DELIVERY_TYPE.IDLE) return

      const providers: DeliveryProvidersT = await fetchActiveDeliveryProviders(storeId)
      setDeliveryProviders(providers.filter(provider => provider.availabilityHours.some(day => isAvailable(day))))
    }
    store?.id && getDeliveryOptions(store.id)
  }, [deliveryType, tableNumber])

  const handleDeliveryChoice = (selectedOption: 'eatin' | 'takeaway') => {
    setDeliveryOption(selectedOption)

    // Filter sub delivery providers based on EATIN or TAKEAWAY
    const targetProviderType = selectedOption === 'eatin' ? DELIVERY_ITEM_TYPE.EATIN : DELIVERY_ITEM_TYPE.TAKEAWAY_PROVIDER
    const matchingSubProviders = deliveryProviders.filter(
      provider => provider.deliveryProviderType === targetProviderType
    )

    if (matchingSubProviders.length > 0) {
      setSubProviders(matchingSubProviders)
      setShowSubProviderOptions(true)
    } else {
      // If no sub providers, select delivery type, close modal and proceed
      const selectedDeliveryType = selectedOption === 'eatin' ? DELIVERY_TYPE.EATIN : DELIVERY_TYPE.TAKEAWAY
      setShowDeliveryModal(false)
      selectDeliveryType({ 
        type: selectedDeliveryType, 
      })
    }
  }

  const handleSubDeliveryChoice = async (provider: DeliveryProviderT) => {
    setLoading(true)
    setSelectedSubProvider(provider)

    try {
      const selectedDeliveryType = provider.deliveryProviderType === DELIVERY_ITEM_TYPE.EATIN ? DELIVERY_TYPE.EATIN : DELIVERY_TYPE.TAKEAWAY
      if (store?.id) {
        selectDeliveryType({ 
        type: selectedDeliveryType, 
        deliveryProviderId: provider.id,
        deliveryProviderType: provider.deliveryProviderType
      })
      }
    } finally {
      setLoading(false)
      setShowDeliveryModal(false)
    }
  }

  return (
    <Page withTopNav={true} withCancelButton={true} withCart={true}>
      <Modal active={showDeliveryModal} maxWidth="400px">
        <div className={styles.deliveryModal}>
          {startupMessage && !deliveryOption && <MessageBanner title={startupMessage.title} description={startupMessage.text} />}
          
          {!showSubProviderOptions ? (
            <div className={styles.deliveryModalButtons}>
              <button className={styles.deliveryModalButton} onClick={() => handleDeliveryChoice('eatin')}>
                <img src={eatinIcon} alt="" className={styles.deliveryModalIcon} />
                Äta här
              </button>
              <button className={styles.deliveryModalButton} onClick={() => handleDeliveryChoice('takeaway')}>
                <img src={takeawayIcon} alt="" className={styles.deliveryModalIcon} />
                Ta med
              </button>
            </div>
          ) : (
            <div className={styles.providerOptions}>
              <h2 className={styles.providerOptionsTitle}>{deliveryDisplayText}</h2>
              <div className={styles.providerOptionsList}>
                {subProviders.map((option) => {
                  const messages: string[] = []
                  option.deliveryTime && messages.push(option.deliveryTime)
                  option.description && messages.push(option.description)

                  return (
                    <DeliveryItem
                      loading={selectedSubProvider?.id === option.id && loading}
                      key={option.id}
                      id={option.id}
                      active={selectedSubProvider?.id === option.id}
                      displayName={option.name}
                      messages={messages}
                      onSelect={() => handleSubDeliveryChoice(option)}
                      price={option.price}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
      {status === "FETCHING" && (
        <Loading heading="Laddar menyer" />
      )}
      <div className={styles.content}>
        <h1 className={styles.heading}>Alla menyer {menuItems && menuItems.length > 5 && (
          <span>({menuItems.length.toString()})</span>
        )}</h1>
        <ul className={styles.list}>
          {menuItems.map(({ id, name, description, image, openingHours, restaurantMessage }) => {
            const { isOpen, openingDetails } = handleOpeningHours(openingHours)
            return (
              <MenuItem key={id} href={`${location.pathname}/${id}`} name={name} description={description} image={image} isOpen={isOpen} openingDetails={openingDetails} message={restaurantMessage} />
            )
          })}
        </ul>
      </div>
    </Page>
  )
}

function mapState (state: RootState) {
  return {
    status: state.menus.status,
    menuItems: state.menus.items,
    store: state.store.current,
    deliveryType: state.cart.deliveryType,
    tableNumber: state.cart.tableNumber
  };
}

function mapDispatch (dispatch: Dispatch) {
  return {
    selectDeliveryType: dispatch.cart.selectDeliveryType,
    fetchActiveDeliveryProviders: dispatch.store.fetchActiveDeliveryProviders,
  };
}

export default connect(mapState, mapDispatch)(Menus)